import { memo, useMemo } from 'react';
import { CircularProgressbar } from 'react-circular-progressbar';
import { type TFunction } from 'i18next';
import { useSecurityScore } from '../../hooks/useSecurityScore';
import Grid, { IGridProps } from '../Grid';
import Typography, { TYPOGRAPHY_H5 } from '../Typography';
import { S2, S3, S5 } from '../../theme';
import { mergeSX } from '../../services/helpers';
import { ShieldImage } from '../../images';

export interface ISecurityScoreWidgetProps extends IGridProps {
  percentage: number;
  t: TFunction<'translation'>;
}

const buildSX = () => ({
  background: S2,
  borderRadius: 4,
  width: 163,
  height: 139,
  position: 'relative',
});

const buildCircularProgressbarStyles = (widgetColor: string) => ({
  root: {
    width: '52%',
    margin: 'auto',
    marginTop: 24,
  },
  path: {
    stroke: widgetColor,
    strokeLinecap: 'round',
    transform: 'rotate(0.65turn)',
    transformOrigin: 'center',
  },
  trail: {
    stroke: S3,
    strokeLinecap: 'round',
    transform: 'rotate(0.65turn)',
    transformOrigin: 'center',
  },
  text: {
    fill: S5,
    ...TYPOGRAPHY_H5,
  },
});

const SecurityScoreWidget = (props: ISecurityScoreWidgetProps) => {
  const {
    percentage, sx, t, ...rest
  } = props;

  const { widgetColor, widgetAdditionalColor, widgetTranslationKey } = useSecurityScore({
    score: percentage,
  });
  const styles = useMemo(() => buildCircularProgressbarStyles(widgetColor), [widgetColor]);

  return (
    <Grid sx={mergeSX(buildSX(), sx)} {...rest}>
      <Grid position="absolute" top={-30} right={-35}>
        <ShieldImage color={widgetColor} additionalColor={widgetAdditionalColor} />
      </Grid>
      <Grid display="flex">
        <CircularProgressbar
          value={percentage}
          strokeWidth={6}
          text={`${percentage}%`}
          circleRatio={0.7}
          styles={styles as object}
        />
      </Grid>
      <Grid textAlign="center">
        <Typography variant="caption" color={S5}>{t(widgetTranslationKey)}</Typography>
      </Grid>
    </Grid>
  );
};

export default memo(SecurityScoreWidget);
